<script setup lang="ts">
import FinestCard from '@/components/FinestCard.vue'
import FinestField from '@/components/FinestField.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import PropertyStepErrors from '@/components/portfolio/PropertyStepErrors.vue'
import { missingWhen } from '@/utils/field-state'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    spaceLabel: string
    errors: string[]
    showMissing?: boolean
  }>(),
  { showMissing: false },
)

const spaceSize = defineModel<number | null>('spaceSize', { default: null })
const numberOfUnits = defineModel<number | null>('numberOfUnits', { default: null })
const actualRent = defineModel<number | null>('actualRent', { default: null })
const expectedRent = defineModel<number | null>('expectedRent', { default: null })

const { t, locale } = useI18n({ useScope: 'local' })

const isMissing = (value: number | null): boolean => props.showMissing && value === null
</script>

<template>
  <FinestCard :title="t('title')" icon="pi pi-key">
    <p class="text-ink-2 mb-4 text-sm">{{ t('hint') }}</p>

    <PropertyStepErrors :messages="errors" class="mb-4" />

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FinestField :label="spaceLabel" input-id="space-size" required>
        <FinestInputNumber
          id="space-size"
          v-model="spaceSize"
          suffix=" m²"
          :locale="locale"
          :state="missingWhen(isMissing(spaceSize))"
          :aria-label="spaceLabel" />
      </FinestField>

      <FinestField :label="t('fields.number_of_units')" input-id="number-of-units" required>
        <FinestInputNumber
          id="number-of-units"
          v-model="numberOfUnits"
          :use-grouping="false"
          :locale="locale"
          :state="missingWhen(isMissing(numberOfUnits))"
          :aria-label="t('fields.number_of_units')" />
      </FinestField>

      <FinestField :label="t('fields.actual_rent')" input-id="actual-rent" required :hint="t('hints.actual_rent')">
        <FinestInputNumber
          id="actual-rent"
          v-model="actualRent"
          mode="currency"
          :locale="locale"
          :state="missingWhen(isMissing(actualRent))"
          :aria-label="t('fields.actual_rent')" />
      </FinestField>

      <FinestField :label="t('fields.expected_rent')" input-id="expected-rent" :hint="t('hints.expected_rent')">
        <FinestInputNumber
          id="expected-rent"
          v-model="expectedRent"
          mode="currency"
          :locale="locale"
          :aria-label="t('fields.expected_rent')" />
      </FinestField>
    </div>
  </FinestCard>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Einheiten & Miete",
    "hint": "Fläche, Einheiten und Mieteinnahmen sind die Grundlage für die Bewertung durch die Bank.",
    "fields": {
      "number_of_units": "Wie viele Einheiten werden erworben?",
      "actual_rent": "Aktuelle Mieteinnahmen",
      "expected_rent": "Geplante Mieteinnahmen"
    },
    "hints": {
      "actual_rent": "Monatliche Nettokaltmiete aller Einheiten.",
      "expected_rent": "Optional – monatliche Nettokaltmiete nach Vermietung oder Modernisierung."
    }
  },
  "en": {
    "title": "Units & rent",
    "hint": "Space, units and rental income are the basis for the bank's assessment.",
    "fields": {
      "number_of_units": "How many units are being purchased?",
      "actual_rent": "Current rental income",
      "expected_rent": "Planned rental income"
    },
    "hints": {
      "actual_rent": "Monthly net cold rent across all units.",
      "expected_rent": "Optional – monthly net cold rent after letting or modernisation."
    }
  }
}
</i18n>
